// MUI components
import {ColumnData} from "../../../../assets/types/ColumnData";
import {OrderListingTableRowData} from "../../../../assets/types/OrderListingTableRowData";
import FaiBox from "../../../../MuiComponents/FaiBox";
import {NavLink} from "react-router-dom";
import FormField from "../../../../components/FormField";
import React from "react";
import Icon from "@mui/material/Icon";
import FaiButton from "../../../../MuiComponents/FaiButton";
import {Chip} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import FaiTypography from "../../../../MuiComponents/FaiTypography";
import TrackShipmentModal from "../component/TrackShipmentModal";
import { statusLook } from "../../../../utils/orderStatus";
import OrderShipActions, { nextStep } from "../../../../components/OrderShipActions";

// Value shape produced by transformOrderData()'s `action` field.
type ActionValue = {
    subOrderId?: string;
    status?: string;
    shipmentId?: string | null;
    order?: any;
    onDone?: () => void;
};

// A document Shiprocket made for the parcel (shipping label / tax invoice):
// a download button once it exists, a dash until then.
const DocumentCell = ({ url, icon, title }: { url?: string | null; icon: string; title: string }) =>
    url ? (
        <IconButton component="a" href={url} target="_blank" rel="noreferrer" title={title} aria-label={title}>
            <Icon fontSize="medium" sx={{ color: "black !important" }}>{icon}</Icon>
        </IconButton>
    ) : (
        <FaiTypography variant="caption" sx={{ color: "text.secondary" }}>—</FaiTypography>
    );

const ActionSelect = ({ value }: { value?: ActionValue }) => {
    const [trackOpen, setTrackOpen] = React.useState(false);
    // A shipment exists once Shiprocket has returned a shipmentId — only then
    // is there anything to track. Rows without one show the plain menu.
    // Older orders carry a shipment id of 0, which is not a real shipment.
    const hasShipment =
        !!value?.subOrderId && !!value?.shipmentId && value.shipmentId !== "-" && String(value.shipmentId) !== "0";

    return (
        <>
            <FaiBox display={"flex"} alignItems={"center"} gap={1}>
                {value?.order && <OrderShipActions order={value.order} onDone={value.onDone ?? (() => {})} />}
                {hasShipment && (
                    <FaiButton
                        // FaiButton defaults ownerState.color to "white", which renders
                        // white label text on the white row — the button looked empty.
                        // Pass an explicit colour so the label is visible.
                        color="info"
                        variant="outlined"
                        size="small"
                        onClick={() => setTrackOpen(true)}
                        sx={{ textTransform: "none", fontSize: 12, py: 0.4, px: 1.2, whiteSpace: "nowrap" }}
                    >
                        Track
                    </FaiButton>
                )}
                {!hasShipment && !(value?.order && nextStep(value.order)) && (
                    <FaiTypography variant="caption" sx={{ color: "text.secondary" }}>—</FaiTypography>
                )}
            </FaiBox>

            {trackOpen && value?.subOrderId && (
                <TrackShipmentModal
                    open={trackOpen}
                    onClose={() => setTrackOpen(false)}
                    subOrderId={value.subOrderId}
                    shipmentId={value.shipmentId}
                />
            )}
        </>
    );
};


const columns: ColumnData[] = [
    { Header: "Order Id", accessor: "orderId" },
    { Header: "Date", accessor: "orderDate" },
    { Header: "Customer", accessor: "customer" },
    { Header: "Product Name", accessor: "productName" },
    { Header: "SKU", accessor: "productSku" },
    { Header: "Qty", accessor: "qty" },
    { Header: "Mode", accessor: "mode" },
    { Header: "MRP (unit)", accessor: "mrp" },
    { Header: "Total", accessor: "total" },
    { Header: "Delivery", accessor: "delivery" },
    {
        Header: "Status", accessor: "status",
        // Same colours as the installed app, so a status reads the same everywhere.
        Cell: ({ value }: { value?: string }) => {
            const look = statusLook(value);
            return (
                <Chip
                    size="small"
                    label={look.label}
                    sx={{ bgcolor: look.bg, color: look.fg, fontWeight: 700, border: `1px solid ${look.accent}` }}
                />
            );
        }
    },
    { Header: "Shipment ID", accessor: "shipmentId" },
    {
        Header: "Label", accessor: "label",
        Cell: ({ value }: { value?: string | null }) => <DocumentCell url={value} icon="download" title="Download shipping label" />
    },
    {
        Header: "Invoice", accessor: "invoice",
        Cell: ({ value }: { value?: string | null }) => <DocumentCell url={value} icon="receipt_long" title="Download invoice" />
    },
    {
        Header: "Action", accessor: "action",
        Cell: ({ value }: { value?: ActionValue }) => <ActionSelect value={value} />
    },
];

const rows: OrderListingTableRowData[] = [
    {
        orderId: 1,
        productName: ["Cardigan"],
        productSku: 1,
        mrp: "Rs. 1000",
        status: "delivered",
        action: "delivered",
        label: null,
        invoice: null,
        mode: "Cash",
    },
];

const dataTableData = {
    columns,
    rows,
};

export default dataTableData;
